import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  Tooltip,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import api from '../utils/api';
import { useDebounce } from '../hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import ChecklistBuilder from '../components/checklists/ChecklistBuilder';
import TablePagination from '@mui/material/TablePagination';

interface Checklist {
  id: number;
  title: string;
  description: string;
  category: string;
  version: number;
  is_active: boolean;
  is_current_version: boolean;

  require_all_items: boolean;
  minimum_completion_percentage: number;
  total_items: number;
  required_items: number;
  created_at: string;
  updated_at: string;
  created_by?: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  };
}

const categoryOptions = [
  { value: 'safety', label: 'Safety' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'quality', label: 'Quality Control' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'audit', label: 'Audit' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'training', label: 'Training' },
  { value: 'other', label: 'Other' }
];

const Checklists: React.FC = () => {
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState<Checklist | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [checklistToDelete, setChecklistToDelete] = useState<Checklist | null>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showHistoricalVersions, setShowHistoricalVersions] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const hasActiveFilters = Boolean(
    debouncedSearchTerm || categoryFilter || statusFilter || showHistoricalVersions
  );

  useEffect(() => {
    fetchChecklists();
  }, [
    page,
    rowsPerPage,
    debouncedSearchTerm,
    categoryFilter,
    statusFilter,
    showHistoricalVersions,
  ]);

  const fetchChecklists = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('page_size', String(rowsPerPage));
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (categoryFilter) params.append('category', categoryFilter);
      if (statusFilter === 'active') params.append('is_active', 'true');
      if (statusFilter === 'inactive') params.append('is_active', 'false');
      if (showHistoricalVersions) params.append('include_historical', 'true');
      const response = await api.get(`/checklists/?${params.toString()}`);
      const results = response.data.results || response.data;
      setChecklists(results);
      setCount(response.data.count ?? results.length ?? 0);
      setError(null);
    } catch (err) {
      setError('Failed to load checklists. Please try again.');
      console.error('Error fetching checklists:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChecklist = () => {
    setEditingChecklist(null);
    setBuilderOpen(true);
  };

  const handleEditChecklist = (checklist: Checklist) => {
    setEditingChecklist(checklist);
    setBuilderOpen(true);
  };

  const handleDuplicateChecklist = async (checklist: Checklist) => {
    try {
      const response = await api.post(`/checklists/${checklist.id}/duplicate/`);
      if (response.status === 201) {
        fetchChecklists(); // Refresh the list
      }
    } catch (err) {
      setError('Failed to duplicate checklist. Please try again.');
      console.error('Error duplicating checklist:', err);
    }
  };

  const handleToggleActive = async (checklist: Checklist) => {
    try {
      const response = await api.patch(`/checklists/${checklist.id}/`, {
        is_active: !checklist.is_active
      });
      if (response.status === 200) {
        fetchChecklists();
      }
    } catch (err) {
      setError('Failed to update checklist status. Please try again.');
      console.error('Error updating checklist:', err);
    }
  };

  const handleDeleteChecklist = (checklist: Checklist) => {
    setChecklistToDelete(checklist);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!checklistToDelete) return;
    
    try {
      await api.delete(`/checklists/${checklistToDelete.id}/`);
      setDeleteDialogOpen(false);
      setChecklistToDelete(null);
      fetchChecklists();
    } catch (err) {
      setError('Failed to delete checklist. Please try again.');
      console.error('Error deleting checklist:', err);
    }
  };

  const handleBuilderClose = () => {
    setBuilderOpen(false);
    setEditingChecklist(null);
    fetchChecklists(); // Refresh the list
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleCategoryFilterChange = (value: string) => {
    setCategoryFilter(value);
    setPage(0);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleHistoricalVersionsChange = (checked: boolean) => {
    setShowHistoricalVersions(checked);
    setPage(0);
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      safety: 'error',
      compliance: 'warning',
      quality: 'success',
      maintenance: 'info',
      inspection: 'primary',
      audit: 'secondary',
      onboarding: 'default',
      training: 'default',
      other: 'default'
    };
    return colors[category] || 'default';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" component="h1">
            Checklists
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showHistoricalVersions}
                onChange={(e) => handleHistoricalVersionsChange(e.target.checked)}
              />
            }
            label="Show historical versions"
          />
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            onClick={() => navigate('/checklists/completions')}
          >
            Review Completions
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateChecklist}
          >
            Create Checklist
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search checklists"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => handleCategoryFilterChange(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categoryOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="body2" color="text.secondary">
                {count} checklist{count === 1 ? '' : 's'}
                {hasActiveFilters ? ' matching filters' : ''}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Checklists Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checklists.map((checklist) => (
              <TableRow key={checklist.id} hover>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {checklist.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {checklist.description}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={categoryOptions.find(c => c.value === checklist.category)?.label || checklist.category}
                    color={getCategoryColor(checklist.category) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {checklist.total_items} items
                  </Typography>
                  {checklist.required_items > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      ({checklist.required_items} required)
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                      {checklist.version}
                    </Typography>
                    {checklist.is_current_version && (
                      <Chip 
                        label="Current" 
                        size="small" 
                        color="primary"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={checklist.is_active}
                        onChange={() => handleToggleActive(checklist)}
                        size="small"
                      />
                    }
                    label={checklist.is_active ? 'Active' : 'Inactive'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(checklist.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(checklist.updated_at)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" gap={1} justifyContent="center">
                    <Tooltip title="Edit">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleEditChecklist(checklist)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                      <IconButton 
                        size="small" 
                        color="secondary"
                        onClick={() => handleDuplicateChecklist(checklist)}
                      >
                        <DuplicateIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteChecklist(checklist)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={(_e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Empty State */}
      {checklists.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No checklists found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {hasActiveFilters
              ? 'Try adjusting your search or filter criteria'
              : 'Get started by creating your first checklist'}
          </Typography>
          {!hasActiveFilters && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateChecklist}
            >
              Create Your First Checklist
            </Button>
          )}
        </Box>
      )}

      {/* Checklist Builder Modal */}
      <ChecklistBuilder
        open={builderOpen}
        onClose={handleBuilderClose}
        checklist={editingChecklist}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Checklist</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{checklistToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Checklists; 